import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { FormError, FormLabel } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ComplianceStatus } from '@/enum/global.enum'
import { cn } from '@/lib/utils'
import { Check, CheckIcon, ChevronDownIcon, Search } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

type GenericSelectProps<T extends Record<string, string | number>> = {
  value: T[keyof T] | (Omit<T[keyof T], 'ALL'> & 'ALL')
  error?: string
  handleChange: (value: T[keyof T] | (Omit<T[keyof T], 'ALL'> & 'ALL')) => void
  label?: string
  labelInfo?: ReactNode
  required?: boolean
  hideOptions?: T[keyof T][]
  disabledOptions?: T[keyof T][]
  options: T
  placeholder?: string
  showAll?: boolean
  className?: string
  disabled?: boolean
  bgColorOptions?: Partial<Record<T[keyof T] | 'ALL', string>>
  variant?: 'default' | 'badge'
  customLabels?: Partial<Record<T[keyof T] | 'ALL', string>>
  dataFieldName?: string
  zIndex?: string
  /** Enables an in-dropdown search input (same UX as GenericAPISelect). */
  searchable?: boolean
  searchPlaceholder?: string
  helpertext?: string
}

const GenericSelect = <T extends Record<string, string | number>>({
  value,
  error,
  handleChange,
  label,
  labelInfo,
  required = false,
  hideOptions = [],
  disabledOptions = [],
  options,
  showAll = false,
  placeholder = 'Select an option',
  className = 'w-full',
  disabled = false,
  bgColorOptions,
  variant = 'default',
  customLabels,
  dataFieldName,
  zIndex = 'z-[9999]',
  searchable = false,
  searchPlaceholder = 'Search here...',
  helpertext,
}: GenericSelectProps<T>) => {
  const [selectedValue, setSelectedValue] = useState<string | number>(value)
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    // Sync when parent value changes (e.g. form reset while mounted)
    setSelectedValue(value)
  }, [value])

  useEffect(() => {
    if (!open) setSearchTerm('')
  }, [open])

  const getDisplayLabel = (option: string | number) => {
    const optionValue = option as T[keyof T]
    return (
      customLabels?.[optionValue] ??
      (option === ComplianceStatus.OFI
        ? 'Opportunity for Improvement (OFI)'
        : option === 'true'
          ? 'YES'
          : option === 'false'
            ? 'NO'
            : option.toString().replace(/_/g, ' ').toLowerCase())
    )
  }

  const visibleOptions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    return Object.values(options).filter((option: string | number) => {
      const optionValue = option as T[keyof T]
      const isHiddenOption = hideOptions.includes(optionValue)
      const isSelectedOption = option.toString() === selectedValue?.toString()
      if (isHiddenOption && !isSelectedOption) return false

      if (!searchable || !query) return true

      const labelText = getDisplayLabel(option).toString().toLowerCase()
      const valueText = option.toString().toLowerCase().replace(/_/g, ' ')
      return labelText.includes(query) || valueText.includes(query)
    })
  }, [options, hideOptions, selectedValue, searchable, searchTerm, customLabels])

  const selectOption = (nextValue: string) => {
    setSelectedValue(nextValue)
    handleChange(nextValue as T[keyof T] | (Omit<T[keyof T], 'ALL'> & 'ALL'))
    window.setTimeout(() => setOpen(false), 0)
  }

  const triggerClassName = cn(
    `text-[13px]
            ${error ? 'border-[0.5px] border-red-500' : ''} ${className}
          ${
            variant === 'badge'
              ? 'rounded-sm py-0 px-1 text-xs whitespace-nowrap shrink-0 data-[size=default]:h-6.5 font-normal cursor-pointer'
              : ''
          }
         `
  )

  if (searchable) {
    return (
      <div data-field-name={dataFieldName}>
        {label ? (
          <FormLabel required={required} info={labelInfo}>
            {label}
          </FormLabel>
        ) : null}
        <Select>
          <SelectTrigger
            disabled={disabled}
            icon={variant === 'badge' ? <ChevronDownIcon className='size-3' /> : undefined}
            onClick={(e) => {
              e.stopPropagation()
              setOpen(!open)
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className={triggerClassName}
          >
            <div
              role='combobox'
              aria-expanded={open}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis w-full font-light',
                selectedValue ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {selectedValue ? (
                <>
                  {bgColorOptions?.[selectedValue as T[keyof T]] ? (
                    <div
                      className='w-4 h-4 rounded-sm shrink-0'
                      style={{ backgroundColor: bgColorOptions[selectedValue as T[keyof T]] }}
                    />
                  ) : null}
                  <span className='truncate'>{getDisplayLabel(selectedValue)}</span>
                </>
              ) : (
                <span className='truncate'>{placeholder}</span>
              )}
            </div>
          </SelectTrigger>

          {open && (
            <SelectContent
              className={cn(zIndex, 'overflow-hidden max-w-[var(--radix-select-trigger-width)]')}
              onCloseAutoFocus={(e) => e.preventDefault()}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerDownOutside={() => setOpen(false)}
              onEscapeKeyDown={() => setOpen(false)}
            >
              <Command className='overflow-hidden min-h-[200px]' shouldFilter={false}>
                <Input
                  className='border-none rounded-none h-[35px]'
                  required
                  type='search'
                  value={searchTerm}
                  placeholder={searchPlaceholder}
                  icon={<Search className='w-4 h-4' />}
                  onChange={(e) => setSearchTerm(e.target?.value)}
                />
                <hr className='dark:bg-slate-500 h-[1px] w-full border-none' />
                <CommandList className='max-h-[200px] pt-1'>
                  <CommandEmpty>Not found.</CommandEmpty>
                  <CommandGroup>
                    {showAll && (
                      <CommandItem
                        key='ALL'
                        value='ALL'
                        onSelect={() => {
                          selectOption('ALL')
                        }}
                      >
                        All
                        <Check
                          className={cn('ml-auto mt-[4px]', selectedValue === 'ALL' ? 'opacity-100' : 'opacity-0')}
                        />
                      </CommandItem>
                    )}
                    {visibleOptions.map((option: string | number) => {
                      const optionValue = option as T[keyof T]
                      const bgColor = bgColorOptions?.[optionValue]
                      const displayLabel = getDisplayLabel(option)
                      const isSelected = option.toString() === selectedValue?.toString()

                      return (
                        <CommandItem
                          key={option}
                          value={`${option} ${displayLabel}`}
                          disabled={disabledOptions.includes(optionValue)}
                          className={cn('min-w-0', hideOptions.includes(optionValue) && 'hidden')}
                          onSelect={() => {
                            selectOption(option.toString())
                          }}
                        >
                          <span
                            className={cn(
                              customLabels?.[optionValue] ? 'whitespace-nowrap' : 'whitespace-nowrap capitalize',
                              'flex gap-2 items-center min-w-0 flex-1'
                            )}
                          >
                            {bgColor ? (
                              <div className='w-4 h-4 rounded-sm shrink-0' style={{ backgroundColor: bgColor }} />
                            ) : null}
                            <span className='truncate'>{displayLabel}</span>
                          </span>
                          <Check
                            className={cn('ml-auto shrink-0 mt-[4px]', isSelected ? 'opacity-100' : 'opacity-0')}
                          />
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </SelectContent>
          )}
        </Select>
        <FormError error={error} />
      </div>
    )
  }

  return (
    <div data-field-name={dataFieldName}>
      {label ? (
        <FormLabel required={required} info={labelInfo}>
          {label}
        </FormLabel>
      ) : (
        ''
      )}
      <Select
        onValueChange={(e) => {
          setSelectedValue(e)
          handleChange(e as T[keyof T] | (Omit<T[keyof T], 'ALL'> & 'ALL'))
        }}
        value={selectedValue?.toString()}
        disabled={disabled}
      >
        <SelectTrigger
          icon={variant === 'badge' ? <ChevronDownIcon className='size-3' /> : undefined}
          className={triggerClassName}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className={zIndex}>
          {showAll && (
            <SelectItem
              icon={variant === 'badge' ? <CheckIcon className='size-3.5' /> : undefined}
              key={'ALL'}
              className='text-[13px] rounded-none cursor-pointer'
              value={'ALL'}
            >
              <span className='whitespace-nowrap'>All</span>
            </SelectItem>
          )}
          {Object.values(options).map((option: string | number) => {
            const optionValue = option as T[keyof T]
            const isHiddenOption = hideOptions.includes(optionValue)
            const isSelectedOption = option.toString() === selectedValue?.toString()

            if (isHiddenOption && !isSelectedOption) return null

            const bgColor = bgColorOptions?.[optionValue]
            const displayLabel = getDisplayLabel(option)
            return (
              <SelectItem
                key={option}
                className={cn('text-[13px] rounded-none cursor-pointer', isHiddenOption && 'hidden')}
                value={option.toString()}
                disabled={disabledOptions.includes(optionValue)}
                icon={variant === 'badge' ? <CheckIcon className='size-3.5' /> : undefined}
              >
                <span
                  className={cn(
                    customLabels?.[optionValue] ? 'whitespace-nowrap' : 'whitespace-nowrap capitalize',
                    'flex gap-2 items-center'
                  )}
                >
                  {bgColor ? <div className='w-4 h-4 rounded-sm' style={{ backgroundColor: bgColor }}></div> : null}
                  {displayLabel}
                </span>
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      {helpertext ? <p className='text-muted-foreground text-xs mt-1'>{helpertext}</p> : null}
      <FormError error={error} />
    </div>
  )
}

export default GenericSelect
